import {UserModel} from 'app/models/UserModel'
import {decodeJwtPayload} from 'common/helpers/supportSessionToken'
import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {RootState} from 'setup'

type SupportUser = {
  id: string
  username: string
  email: string
}

// The tenant user the support session acts as. Normally `userReducer.user`; when that
// is not loaded yet, the identity claims carried by the token itself.
type ActingUser = Pick<UserModel, 'username' | 'email'> & Partial<UserModel>

type UseSupportUserResult = {
  isSupportSession: boolean
  supportUser: SupportUser | null
  actingUser: ActingUser | null
}

export const useSupportUser = (): UseSupportUserResult => {
  const user = useSelector<RootState>(({userReducer}) => userReducer.user) as UserModel | null
  const accessToken = useSelector<RootState>(({userReducer}) => userReducer.accessToken) as
    | string
    | null

  const supportContext = useMemo<UseSupportUserResult>(() => {
    const payload = decodeJwtPayload(accessToken)

    if (!payload?.support_user_id) {
      return {isSupportSession: false, supportUser: null, actingUser: user}
    }

    // A support session is whatever the token says it is. `userReducer.user` is not
    // populated on every screen (create-password only stores the token, and the
    // onboarding WizardLayout never calls /me - only MasterLayout does), so fall back
    // to the identity claims the token carries instead of hiding the banner there.
    const actingUser: ActingUser = user ?? {
      username: payload.username ?? '',
      email: payload.email ?? '',
    }

    return {
      isSupportSession: true,
      supportUser: payload.support_user ?? null,
      actingUser,
    }
  }, [user, accessToken])

  return supportContext
}
